import { useState } from 'react'
import type { DailyReportResponse } from 'api/generated'
import { Button } from 'components/ui/button'
import {
  type Granularity,
  axisProps,
  changePct,
  formatPeriod,
  formatRowDate,
} from 'helpers/daily-report'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { MetricTile } from './MetricTile'

// GA4 measures each pair on two properties: the web app (site) and the native app.
// The magazine is a third property and stays out of the split.
// `note`: how the period total reads (daily users summed count a returning user once per day)
const PAIRS = [
  { key: 'views', label: 'Views', site: 'site_views', app: 'app_views' },
  {
    key: 'active_users',
    label: 'Utenti attivi',
    site: 'site_active_users',
    app: 'app_active_users',
    note: 'somma degli attivi di ogni giorno',
  },
  {
    key: 'sessions',
    label: 'Sessioni',
    site: 'site_sessions',
    app: 'app_sessions',
  },
  {
    key: 'new_users',
    label: 'Views nuovi utenti',
    site: 'site_new_user_views',
    app: 'app_new_user_views',
  },
  {
    key: 'non_registered',
    label: 'Views non registrati',
    site: 'site_views_non_registered',
    app: 'app_views_non_registered',
  },
] as const

const SITE_COLOR = 'var(--chart-2)'
const APP_COLOR = 'var(--chart-1)'
const TOTAL_COLOR = 'var(--foreground)'

const integer = new Intl.NumberFormat('it-IT', { maximumFractionDigits: 0 })
const pct = new Intl.NumberFormat('it-IT', { maximumFractionDigits: 1 })
const n = (value: number | null | undefined) =>
  value == null ? '–' : integer.format(value)
const p = (value: number | null | undefined) =>
  value == null ? '–' : `${pct.format(value)}%`

type Point = {
  date: string
  site: number | null
  app: number | null
  total: number | null
  sitePct: number | null
  appPct: number | null
}

const share = (part: number | null, total: number | null) =>
  part == null || !total ? null : (part / total) * 100

// Both properties are needed for a total and a share: one missing = no total
const toPoint = (
  date: string,
  site: number | null | undefined,
  app: number | null | undefined
): Point => {
  const total = site != null && app != null ? site + app : null
  return {
    date,
    site: site ?? null,
    app: app ?? null,
    total,
    sitePct: share(site ?? null, total),
    appPct: share(app ?? null, total),
  }
}

const PointTooltip = ({
  active,
  payload,
  granularity,
}: {
  active?: boolean
  payload?: ReadonlyArray<{ payload?: unknown }>
  granularity: Granularity
}) => {
  const point = payload?.[0]?.payload as Point | undefined
  if (!active || !point) return null
  return (
    <div className='rounded-md border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-sm'>
      <div className='text-muted-foreground'>
        {formatRowDate(point.date, granularity)}
      </div>
      <div className='tabular-nums'>
        App: <b>{n(point.app)}</b> ({p(point.appPct)})
      </div>
      <div className='tabular-nums'>
        Sito: <b>{n(point.site)}</b> ({p(point.sitePct)})
      </div>
      <div className='tabular-nums'>
        Totale: <b>{n(point.total)}</b>
      </div>
    </div>
  )
}

const LegendDot = ({ color, label }: { color: string; label: string }) => (
  <span className='inline-flex items-center gap-1'>
    <span className='h-2 w-2 rounded-sm' style={{ background: color }} />
    {label}
  </span>
)

/** Google Analytics split by app and web site, with their sum and daily share. */
export const AppVsWeb = ({ report }: { report: DailyReportResponse }) => {
  const [pairKey, setPairKey] = useState<(typeof PAIRS)[number]['key']>(
    PAIRS[0].key
  )
  const pair = PAIRS.find((x) => x.key === pairKey) ?? PAIRS[0]
  const totalNote = 'note' in pair ? pair.note : 'totale'
  const granularity = report.period.granularity
  const unit = granularity === 'day' ? 'giorno' : 'settimana'

  const points = report.rows.map((row) =>
    toPoint(row.date, row.values[pair.site], row.values[pair.app])
  )
  const current = toPoint(
    report.period.from,
    report.summary[pair.site],
    report.summary[pair.app]
  )
  const previous = toPoint(
    report.previous_period.from,
    report.previous_summary[pair.site],
    report.previous_summary[pair.app]
  )
  const changeTitle = `Rispetto a ${formatPeriod(report.previous_period.from, report.previous_period.to)}`
  const pointsDiff = (now: number | null, before: number | null) =>
    now == null || before == null
      ? ''
      : ` (${now >= before ? '+' : ''}${pct.format(now - before)} punti)`

  if (report.rows.length === 0)
    return (
      <p className='text-sm text-muted-foreground'>Nessun dato nel periodo.</p>
    )

  return (
    <div className='space-y-3'>
      <div className='flex flex-wrap items-center gap-1'>
        {PAIRS.map((option) => (
          <Button
            key={option.key}
            size='sm'
            className='h-7 px-2 text-xs'
            variant={pairKey === option.key ? 'secondary' : 'ghost'}
            onClick={() => setPairKey(option.key)}
          >
            {option.label}
          </Button>
        ))}
      </div>

      <div className='grid grid-cols-2 gap-2 sm:grid-cols-4'>
        <MetricTile
          label={`App: ${pair.label.toLowerCase()}`}
          value={n(current.app)}
          note={totalNote}
          change={changePct(current.app, previous.app)}
          changeTitle={changeTitle}
        />
        <MetricTile
          label={`Sito: ${pair.label.toLowerCase()}`}
          value={n(current.site)}
          note={totalNote}
          change={changePct(current.site, previous.site)}
          changeTitle={changeTitle}
        />
        <MetricTile
          label={`App + sito: ${pair.label.toLowerCase()}`}
          value={n(current.total)}
          note={totalNote}
          change={changePct(current.total, previous.total)}
          changeTitle={changeTitle}
        />
        <MetricTile
          label='Quota app / sito'
          value={`${p(current.appPct)} / ${p(current.sitePct)}`}
          note={`app nel periodo${pointsDiff(current.appPct, previous.appPct)}`}
          title={changeTitle}
        />
      </div>

      <div className='grid gap-3 lg:grid-cols-2'>
        <div className='rounded-lg border p-4'>
          <h3 className='text-sm font-semibold'>App, sito e totale</h3>
          <p className='text-xs text-muted-foreground'>Valore per {unit}</p>
          <div className='mt-2 flex gap-3 text-xs text-muted-foreground'>
            <LegendDot color={APP_COLOR} label='App' />
            <LegendDot color={SITE_COLOR} label='Sito' />
            <LegendDot color={TOTAL_COLOR} label='Totale' />
          </div>
          <div className='mt-2 h-[220px]'>
            <ResponsiveContainer width='100%' height='100%'>
              <LineChart
                data={points}
                margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
              >
                <CartesianGrid
                  vertical={false}
                  stroke='var(--border)'
                  strokeDasharray='3 3'
                />
                <XAxis
                  dataKey='date'
                  tickFormatter={(d: string) =>
                    formatRowDate(d, granularity).replace('Sett. ', '')
                  }
                  minTickGap={24}
                  {...axisProps}
                />
                <YAxis
                  tickFormatter={(v: number) => integer.format(v)}
                  width={64}
                  domain={[0, 'auto']}
                  {...axisProps}
                />
                <Tooltip
                  cursor={{ stroke: 'var(--muted-foreground)' }}
                  content={({ active, payload }) => (
                    <PointTooltip
                      active={active}
                      payload={payload}
                      granularity={granularity}
                    />
                  )}
                />
                {(
                  [
                    ['app', APP_COLOR],
                    ['site', SITE_COLOR],
                    ['total', TOTAL_COLOR],
                  ] as const
                ).map(([key, color]) => (
                  <Line
                    key={key}
                    type='monotone'
                    dataKey={key}
                    stroke={color}
                    strokeWidth={2}
                    strokeDasharray={key === 'total' ? '4 3' : undefined}
                    dot={points.length === 1}
                    activeDot={{ r: 4 }}
                    connectNulls
                    isAnimationActive={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className='rounded-lg border p-4'>
          <h3 className='text-sm font-semibold'>Quota app e sito</h3>
          <p className='text-xs text-muted-foreground'>
            Percentuale del totale per {unit}
          </p>
          <div className='mt-2 flex gap-3 text-xs text-muted-foreground'>
            <LegendDot color={APP_COLOR} label='App' />
            <LegendDot color={SITE_COLOR} label='Sito' />
          </div>
          <div className='mt-2 h-[220px]'>
            <ResponsiveContainer width='100%' height='100%'>
              <BarChart
                data={points}
                margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
              >
                <CartesianGrid
                  vertical={false}
                  stroke='var(--border)'
                  strokeDasharray='3 3'
                />
                <XAxis
                  dataKey='date'
                  tickFormatter={(d: string) =>
                    formatRowDate(d, granularity).replace('Sett. ', '')
                  }
                  minTickGap={24}
                  {...axisProps}
                />
                <YAxis
                  tickFormatter={(v: number) => `${v}%`}
                  width={48}
                  domain={[0, 100]}
                  ticks={[0, 25, 50, 75, 100]}
                  {...axisProps}
                />
                <Tooltip
                  cursor={{ fill: 'var(--muted)' }}
                  content={({ active, payload }) => (
                    <PointTooltip
                      active={active}
                      payload={payload}
                      granularity={granularity}
                    />
                  )}
                />
                <Bar
                  dataKey='appPct'
                  stackId='share'
                  fill={APP_COLOR}
                  maxBarSize={32}
                  isAnimationActive={false}
                />
                <Bar
                  dataKey='sitePct'
                  stackId='share'
                  fill={SITE_COLOR}
                  maxBarSize={32}
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  )
}
